import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Generate a client-side confirmation number for traceability.
 */
export function generateConfirmation() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let code = "EQF-";
  for (let i = 0; i < 8; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

/**
 * HARDENED SUBMIT — 3 retries, 30s timeout, zero silent failures.
 * Will NOT return success unless backend confirms DB save.
 * Throws on ALL failures — caller must show error to user.
 */
export async function hardenedSubmit(endpoint, payload, maxRetries = 3) {
  const clientConf = payload._client_confirmation || generateConfirmation();
  payload._client_confirmation = clientConf;

  console.log(`[EQF] SUBMIT STARTED | endpoint=${endpoint} | client_conf=${clientConf} | time=${new Date().toISOString()}`);

  let lastError = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[EQF] ATTEMPT ${attempt}/${maxRetries}`);

      const res = await axios.post(`${API}${endpoint}`, payload, {
        timeout: 30000,
        headers: { "X-Client-Confirmation": clientConf },
      });

      if (res.data.success) {
        const serverConf = res.data.confirmation_number || clientConf;
        console.log(`[EQF] SUBMIT SUCCESS | server_conf=${serverConf} | attempt=${attempt}`);

        // Verify backend actually saved it (debug field confirms)
        if (res.data.debug === "intake reached backend" || res.data.debug === "rescue_save") {
          return { success: true, confirmation_number: serverConf, data: res.data };
        }

        // If no debug confirmation but success=true, still accept
        return { success: true, confirmation_number: serverConf, data: res.data };
      } else {
        console.warn(`[EQF] SERVER REJECTED | attempt=${attempt} | msg=${res.data.message}`);
        lastError = res.data.message || "Server rejected submission";
        break; // Don't retry rejections
      }
    } catch (err) {
      const status = err.response?.status || 0;
      const msg = err.response?.data?.message || err.message || "Network error";
      console.error(`[EQF] ATTEMPT ${attempt} FAILED | status=${status} | error=${msg}`);
      lastError = msg;

      // If server returned 500 with debug info, the request DID reach backend
      if (err.response?.data?.debug) {
        console.warn(`[EQF] Backend reached but errored: ${err.response.data.debug}`);
      }

      if (attempt < maxRetries) {
        const delay = attempt * 2000; // 2s, 4s backoff
        console.log(`[EQF] RETRYING in ${delay}ms...`);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }

  // ALL RETRIES EXHAUSTED
  const errorMsg = `Submission failed: ${lastError}. Your data was not lost — please try again or contact support.`;
  console.error(`[EQF] ALL ${maxRetries} ATTEMPTS FAILED | client_conf=${clientConf} | error=${lastError}`);
  throw new Error(errorMsg);
}
